import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pause, Play, Square, Maximize2, Minimize2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Mode = "passive" | "invocation" | "proactive";

interface Props {
  sessionId: string;
  status: string;
  mode: Mode;
  onModeChange: (m: Mode) => void;
  onEnded: () => void;
  fullscreenTargetRef?: React.RefObject<HTMLElement>;
}

export function PresenterControls({ sessionId, status, mode, onModeChange, onEnded, fullscreenTargetRef }: Props) {
  const [busy, setBusy] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const lastModeRef = useRef<Mode>(mode);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const transition = async (new_status: string) => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("jarvis-session", {
        body: { action: "transition", session_id: sessionId, new_status },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error ?? "transition_failed");
    } catch (e) {
      toast.error(`Falha: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const pause = () => transition("paused");
  const resume = () => transition("listening");
  const end = async () => {
    await transition("ending");
    await transition("completed");
    onEnded();
  };

  const toggleFs = async () => {
    const target = fullscreenTargetRef?.current ?? document.documentElement;
    if (!document.fullscreenElement) {
      await target.requestFullscreen?.().catch(() => {});
    } else {
      await document.exitFullscreen?.().catch(() => {});
    }
  };

  const isPaused = status === "paused";
  const canEnd = !["completed", "abandoned", "ending"].includes(status);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={mode}
        onValueChange={(v) => { lastModeRef.current = v as Mode; onModeChange(v as Mode); }}
      >
        <SelectTrigger className="w-[180px] h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="passive">Passivo</SelectItem>
          <SelectItem value="invocation">Invocação</SelectItem>
          <SelectItem value="proactive">Proativo</SelectItem>
        </SelectContent>
      </Select>

      {isPaused ? (
        <Button size="sm" variant="secondary" onClick={resume} disabled={busy}>
          <Play className="w-4 h-4 mr-1" /> Retomar
        </Button>
      ) : (
        <Button size="sm" variant="secondary" onClick={pause} disabled={busy || !["listening", "thinking", "speaking"].includes(status)}>
          <Pause className="w-4 h-4 mr-1" /> Pausar
        </Button>
      )}

      <Button size="sm" variant="outline" onClick={toggleFs}>
        {isFs ? <Minimize2 className="w-4 h-4 mr-1" /> : <Maximize2 className="w-4 h-4 mr-1" />}
        {isFs ? "Sair tela cheia" : "Tela cheia"}
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="destructive" disabled={busy || !canEnd}>
            <Square className="w-4 h-4 mr-1" /> Encerrar
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar sessão?</AlertDialogTitle>
            <AlertDialogDescription>
              A sessão será marcada como concluída e os artefatos pós-sessão serão gerados automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={end}>Encerrar agora</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
